// src/components/CaseStudyGrid.tsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../hooks";
import {
  getCaseStudyCategories,
  getArticlesByCaseStudyCategory,
  getArticlesByCategoryCaseStudy,
} from "../../store/actions";
import ArticleCard from "../ArticleCard";
import LoaderOverlay from "../LoaderOverlay";
import Loader from "../Loader";
import Pagination from "../Pagination";
import FilterBar from "../FilterBar";
import Container from "../Container";
import Title from "../Typography/Title";
import ArticleNotFound from "../../pages/ArticleNotFound";
import BackToTop from "../BackToTop";
import * as S from "./style";
import { Article } from "../../store/actions/types";

export interface RouterState {
  category: string;
}

const CaseStudyGrid: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation<RouterState>();
  const { category } = useParams<RouterState>();

  const articlesPerPage = 9;
  const [page, setPage] = useState(0);
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);

  const {
    error,
    data: { articles, case_study_categories },
    count,
  } = useAppSelector((s) => s.articles);

  useEffect(() => {
    setPage(0);
  }, [selectedTheme]);

  useEffect(() => {
    dispatch(getCaseStudyCategories());
  }, [dispatch]);

  useEffect(() => {
    if (selectedTheme) {
      dispatch(
        getArticlesByCaseStudyCategory({
          category: selectedTheme,
          page,
          articlesPerPage,
        })
      );
    } else {
      dispatch(
        getArticlesByCategoryCaseStudy({
          category,
          page,
          articlesPerPage,
        })
      );
    }
    history.replace(`/case-study${page > 0 ? `?page=${page}` : ""}`);
  }, [dispatch, selectedTheme, category, page]);

  // scroll to top on page change
  useEffect(() => window.scrollTo(0, 0), [page]);

  const handleFilterChange = (theme: string | null) => {
    setSelectedTheme(theme);
    setPage(0);
  };

  if (articles.length === 0 && error.name === "") {
    return <Loader />;
  }
  if (articles.length === 0) {
    return <ArticleNotFound title="No case studies found" />;
  }

  return (
    <>
      <Helmet>
        <title>Unitiva – Case Study</title>
      </Helmet>
      <LoaderOverlay isWhite />
      <Container>
        <S.CaseStudyContainerFlex>
          <Title title="I nostri case studies" size="4" margin={[0, 0, 16]} />
          <FilterBar
            themes={case_study_categories}
            selectedTheme={selectedTheme}
            onFilter={handleFilterChange}
          />
          <S.Articles>
            <S.CaseStudyMainArticlesGrid>
              {articles.map((art: Article) => (
                <ArticleCard
                  key={art.id}
                  isMain
                  isOneArticle={articles.length === 1}
                  {...art}
                  onFilterByTheme={handleFilterChange}
                />
              ))}
            </S.CaseStudyMainArticlesGrid>
          </S.Articles>
          <Pagination
            category="case-study"
            tag={undefined}
            articlesPerPage={articlesPerPage}
            page={page}
            filteredArticlesCount={count}
            theme={selectedTheme}
            setPage={setPage}
          />
        </S.CaseStudyContainerFlex>
      </Container>
      <BackToTop />
    </>
  );
};

export default CaseStudyGrid;
